import { createContext, useContext, useEffect, useState } from "react";
import { setSession } from "../utils/auth.js";
import { clearDiscoverySessionCache } from "@/pages/dashboard/intelligence/discovery/_lib/sessionCache.js";
import * as API from '../services/api'
import localStorageAvailable from "../utils/localStorageAvailable.js";
import { AnalyticsService, EVENT_NAMES } from "@/services/analytics.js";
import { AUTH_BASE_PATH, AUTH_RETURN_URL_KEY, MCP_CONNECT_RETURN_URL_KEY } from '@/consts/index.js'
// import Intercom from '@intercom/messenger-js-sdk'

export const AuthContext = createContext(null);


export function AuthProvider({ children }) {

    const [user, setUser] = useState(null)
    const [accounts, setAccounts] = useState([])
    const [spaces, setSpaces] = useState([])
    const [spaceUsersMap, setSpaceUsersMap] = useState({})
    const [accountUsersMap, setAccountUsersMap] = useState({})

    const [isInitialized, setIsInitialized] = useState(false)
    const queryParams = new URLSearchParams(window.location.search)
    const avoidAuthenticateOnInit = queryParams.get('customFlow') // Meaning we intend to override the default behavior of the auth flow. we do it for colab team to be able to log in behalf of users.

    const getAuthEntitiesAndSetSession = async (userData) => {

        const [accountsOfUserResponse, spacesOfUserResponse] = await Promise.all([
            API.getUserAccountsByJwt(userData.jwt),
            API.getSpacesByJwt(userData.jwt)
        ])

        // an 'Orphan' user means the user has no accounts or spaces - it could happen if the user has been deleted from a space and not assign to any account
        const isOrphanUser = !accountsOfUserResponse.data?.length && spacesOfUserResponse.data?.length
        if (isOrphanUser) {
            // TODO - implement this. need to display a form to register new account
            alert('You have been removed from all accounts and spaces. Please contact your team to add you back.')
            logout()
            return
        }

        // Set the spaces and accounts to local state
        setSpaces(spacesOfUserResponse.data ? spacesOfUserResponse.data.sort((a, b) => a.name.localeCompare(b.name)) : [])
        setAccounts(accountsOfUserResponse.data)
        // Set session
        setSession(userData.jwt)
        setUser(userData)
        setIsInitialized(true)

        // McpConnectPage stashes the full URL it was on (path + query, e.g.
        // /mcp/connect?request_id=...) before bouncing an unauthenticated user
        // through this same login/register flow. Every login/register path here
        // hardcodes its own post-auth destination (BASE_PATH_AFTER_SUCCESSFUL_AUTH),
        // so this is the one place shared by all of them where we can hand the user
        // back to where they actually meant to go. A hard redirect (not react-router
        // navigate) so it wins the race against whatever navigate() the caller does
        // right after this resolves.
        const mcpConnectReturnUrl = sessionStorage.getItem(MCP_CONNECT_RETURN_URL_KEY)
        if (mcpConnectReturnUrl) {
            sessionStorage.removeItem(MCP_CONNECT_RETURN_URL_KEY)
            window.location.assign(mcpConnectReturnUrl)
            return
        }

        // Same idea for an in-app deep link (e.g. a shared creative) opened while signed
        // out. Only same-app /account/ paths are honored.
        const authReturnUrl = sessionStorage.getItem(AUTH_RETURN_URL_KEY)
        if (authReturnUrl) {
            sessionStorage.removeItem(AUTH_RETURN_URL_KEY)
            if (authReturnUrl.startsWith('/account/') && window.location.pathname + window.location.search !== authReturnUrl) {
                window.location.assign(authReturnUrl)
            }
        }
    }

    const authenticate = async (userData) => {
        try {
            if (userData) {
                await getAuthEntitiesAndSetSession(userData)
                return
            }

            // Continue auth flow
            if (!localStorageAvailable()) {
                setIsInitialized(true)
                return
            }

            const jwt = localStorage.getItem('jwt')
            if (!jwt) {
                setIsInitialized(true)
                return
            }

            const { data } = await API.getUser(jwt)

            if (!data) {
                localStorage.removeItem('jwt')
                setIsInitialized(true)
                return
            }

            await getAuthEntitiesAndSetSession(data)

        } catch (e) {
            console.log(e)
            setSession(null)
            alert(e.message)
            window.location.replace(AUTH_BASE_PATH)
        }
    }

    const logout = () => {
        AnalyticsService.track(EVENT_NAMES.LOGOUT)
        AnalyticsService.clearSessions()
        clearDiscoverySessionCache()
        setSession()
        window.location.href = window.location.origin + AUTH_BASE_PATH
    }

    /* When the user is authenticated, we send the user's identity to the analytics platforms.
       Account attribution (the account trait + group) is NOT done here: at login a
       multi-account user hasn't chosen an account yet, so we'd only ever know accounts[0].
       That happens in AccountGuard once the chosen account (the one in the URL) resolves. */
    useEffect(() => {
        if (user) {
            const userData = {
                ...user,
                name: user.shopName ? user.shopName : user.name,
            }
            delete userData.jwt // Don't send this to the analytics platforms...
            delete userData.profileImage // Don't send this to the analytics platforms, too much data
            delete userData.password // Don't send this to the analytics platforms, too much data

            // For the User
            AnalyticsService.identify(user._id, userData)

            AnalyticsService.track('user-logged-in', { user: user._id, userName: user.name, userEmail: user.email })
            // try {
            //     Intercom({
            //         app_id: 'yz0ekdp5',
            //         user_id: user._id,
            //         name: user.name,
            //         email: user.email,
            //         created_at: user.createdAt,
            //     })
            // } catch (e) {
            //     console.error(e)
            // }
        }
    }, [user?._id])

    // Use this when user manually change thier details
    const updateUserData = async ({ userName, userEmail, profileImage }) => {
        // TODO - would it work when 'userName' is undefined? or it will ignore it?
        setUser(prev => ({ ...prev, name: userName, email: userEmail, profileImage }))
    }

    const setFeatureDiscovered = (featureKey) => {
        setUser(prev => ({
            ...prev,
            featureDiscovery: { ...prev.featureDiscovery, [featureKey]: false }
        }))
    }

    // Do this after the user invite a member
    const updateAccountInvitees = (accountId, newInvitees) => {
        setAccounts(accounts.map(account => {
            if (account._id === accountId) {
                account.users = [...account.users, newInvitees]
            }
            return account
        }))
    }

    // Do this after the user update their account details
    const updateAccountDetails = (accountId, { name, email, shouldInitializeResearch }) => {
        setAccounts(accounts.map(account => {
            if (account._id === accountId) {
                return { ...account, name, email, shouldInitializeResearch }
            }
            return account
        }))
    }

    const updateAccountRequiresOnboarding = (accountId, requiresOnboarding) => {
        setAccounts(accounts.map(account => {
            if (account._id === accountId) {
                return { ...account, requiresOnboarding }
            }
            return account
        }))
    }

    // Update space on local state
    const updateSpaceState = async (spaceId, data) => {
        setSpaces(spaces.map(space => {
            if (space._id === spaceId) {
                return data
            }
            return space
        }))
    }

    const addSpaceToState = (space) => {
        setSpaces(prev => [...prev, space])
    }

    const removeSpaceFromState = (spaceId) => {
        setSpaces(spaces.filter(space => space._id !== spaceId))
    }

    const setManySpaceUsers = (spaceIdsWithUsers) => {
        const newSpaceUsersMap = JSON.parse(JSON.stringify(spaceUsersMap))
        spaceIdsWithUsers.forEach(space => {
            newSpaceUsersMap[space.spaceId] = space.users
        })
        setSpaceUsersMap(newSpaceUsersMap)
    }

    // Set users for each space
    const setSpaceUsers = (spaceId, users) => {
        setSpaceUsersMap(prev => ({ ...prev, [spaceId]: users }))
    }

    // get users of space
    const getSpaceUsers = (spaceId) => {
        return spaceUsersMap[spaceId] || []
    }

    // Set users for each account
    const setAccountUsers = (accountId, users) => {
        setAccountUsersMap(prev => ({ ...prev, [accountId]: users }))
    }

    // get users of account
    const getAccountUsers = (accountId) => {
        return accountUsersMap[accountId] || []
    }

    // Call to 'authenticate' on the first render
    useEffect(() => {
        if (!avoidAuthenticateOnInit) {
            authenticate()
        }
    }, [])

    return (
        <AuthContext.Provider
            value={{
                user,
                isInitialized,
                accounts,
                setAccounts,
                setAccountUsers,
                getAccountUsers,
                addSpaceToState,
                removeSpaceFromState,
                updateAccountDetails,
                updateAccountRequiresOnboarding,
                updateAccountInvitees,
                updateUserData,
                setFeatureDiscovered,
                updateSpaceState,
                authenticate,
                setSpaceUsers,
                getSpaceUsers,
                setManySpaceUsers,
                spaces,
                logout
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}


export const useAuthContext = () => {
    const context = useContext(AuthContext);

    if (!context) throw new Error('useAuthContext context must be use inside AuthProvider');

    return context;
};
